import { useEffect, useRef, useState, type ReactNode } from "react";
import { ProblemJsonError } from "@pushback-and-chaos/client";
import { BRAND, UNSUBSCRIBE } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { Pill, Rule } from "../ui/primitives.js";

/**
 * The two requests the page makes, one per button; named here for the reason check-in.tsx gives.
 * `unsubscribeAll` posts the same token to `POST /u/:token/all` (stop all, 2026-10-21).
 */
export interface UnsubscribeClient {
  readonly postUnsubscribe: (token: string) => Promise<unknown>;
  readonly unsubscribeAll: (token: string) => Promise<unknown>;
}

type Phase = "ready" | "sending" | "done" | "failed";

/**
 * A refusal the server gives a link it does not verify: the page says so and offers no retry;
 * anything else is the server not answering. For stop all a 404 is not the link's fault: that
 * route is newer than the link (an older API answers 404 while the one-kind route still works),
 * so it is a failure to try again and leaves the other button alone (review, 2026-10-21).
 */
function isBadLink(error: unknown, notFoundIsBad: boolean): boolean {
  if (!(error instanceof ProblemJsonError)) return false;
  if (error.status === 404) return notFoundIsBad;
  return [400, 401, 403, 410].includes(error.status);
}

/**
 * THE UNSUBSCRIBE PAGE (`#/unsubscribe?token=`, CME stage 3b, 2026-10-11):
 * one heading, one sentence, one button, then a done sentence. Nothing
 * happens on arrival: a link prefetcher or a mail scanner that opens the
 * address withdraws nobody's choice, because only the button POSTs the token
 * (one-click mail clients POST the same route directly, RFC 8058). The
 * server answers the same whether or not anything changed, so the page
 * reads "done" either way and a token proves nothing about an address.
 *
 * STOP ALL (2026-10-21; CAN-SPAM's opt-out menu must offer every commercial
 * email at once): below the first, a second, separate sentence and button
 * post the same token to `POST /u/:token/all`, which withdraws every email
 * choice. Each button posts once, has its own done and failed sentence, and
 * neither runs on arrival. The one-kind button goes once its kind is off;
 * once every choice is off, both go, since the link's own kind is among them.
 *
 * A token the server refuses (400: tampered, foreign, the wrong kind) is a
 * bad link, said once with no button, as check-in.tsx says it (2026-10-11):
 * pressing again could never succeed, and both buttons carry the same token,
 * so a refusal on either ends both. Only a request the server did not answer
 * may be tried again.
 *
 * The words are Codex's (2026-10-11, docs/cme/CODEX-BRIEF-MAIL-WORDS-2026-10-11.md),
 * in copy.ts; stop all's are Codex's too (2026-10-21, and 2026-10-28 for the security emails).
 * Same card as the privacy page.
 */
export function UnsubscribePage({ client, token }: { client: UnsubscribeClient; token: string | null }): ReactNode {
  const [phase, setPhase] = useState<Phase>("ready");
  const [allPhase, setAllPhase] = useState<Phase>("ready");
  const [badLink, setBadLink] = useState(false);
  // Each token is posted once per button: a second press before the first answer, or after "done", sends nothing.
  const posted = useRef(false);
  const postedAll = useRef(false);
  // The pressed button goes when its request is done: focus moves to the sentence that replaced it,
  // or a keyboard or screen-reader user is left on the page with nothing in focus.
  const doneRef = useRef<HTMLParagraphElement>(null);
  const allDoneRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (phase === "done") doneRef.current?.focus();
  }, [phase]);
  useEffect(() => {
    if (allPhase === "done") allDoneRef.current?.focus();
  }, [allPhase]);

  const send = async (request: (token: string) => Promise<unknown>, guard: { current: boolean }, set: (phase: Phase) => void, notFoundIsBad: boolean): Promise<void> => {
    if (token === null || guard.current) return;
    guard.current = true;
    set("sending");
    try {
      await request(token);
      set("done");
    } catch (error) {
      if (isBadLink(error, notFoundIsBad)) {
        setBadLink(true);
        return;
      }
      // A request that never reached the server may be tried again.
      guard.current = false;
      set("failed");
    }
  };

  // Called on the client, never detached from it: the API client's methods read `this`.
  const stop = (): Promise<void> => send((t) => client.postUnsubscribe(t), posted, setPhase, true);
  const stopAll = (): Promise<void> => send((t) => client.unsubscribeAll(t), postedAll, setAllPhase, false);

  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <div data-testid="unsubscribe-card" className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12">
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />
        <h1 className="mt-6 mb-0 font-display text-display font-bold">{UNSUBSCRIBE.heading}</h1>
        {/* One live region, present from the first render, around everything that changes: a sentence
            inserted into it is announced, which one inserted with its own role="status" is not, reliably. */}
        <div aria-live="polite">
        {token === null || badLink ? (
          <p className="mt-8 mb-0 font-sans text-body">{UNSUBSCRIBE.badLink}</p>
        ) : allPhase === "done" ? (
          <p ref={allDoneRef} tabIndex={-1} className="mt-8 mb-0 font-sans text-body outline-none">
            {UNSUBSCRIBE.allDone}
          </p>
        ) : (
          <>
            {phase === "done" ? (
              <p ref={doneRef} tabIndex={-1} className="mt-8 mb-0 font-sans text-body outline-none">
                {UNSUBSCRIBE.done}
              </p>
            ) : (
              <>
                <p className="mt-8 mb-0 font-sans text-body">{UNSUBSCRIBE.sentence}</p>
                <div className="mt-8">
                  <Pill onClick={() => void stop()} disabled={phase === "sending"}>
                    {UNSUBSCRIBE.button}
                  </Pill>
                </div>
                {phase === "failed" ? (
                  <p className="mt-4 mb-0 font-sans text-small">{UNSUBSCRIBE.failed}</p>
                ) : null}
              </>
            )}
            <Rule className="mt-10" />
            <p className="mt-8 mb-0 font-sans text-body">{UNSUBSCRIBE.allSentence}</p>
            <div className="mt-8">
              <Pill onClick={() => void stopAll()} disabled={allPhase === "sending"}>
                {UNSUBSCRIBE.allButton}
              </Pill>
            </div>
            {allPhase === "failed" ? (
              <p className="mt-4 mb-0 font-sans text-small">{UNSUBSCRIBE.allFailed}</p>
            ) : null}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
